<template>
  <div class="availability-calendar-wrapper">
    <div v-if="error" class="app-modal-alert app-modal-alert--error">
      Die Verfügbarkeitsdaten konnten nicht geladen werden.
    </div>

    <template v-else>
      <div
        class="availability-calendar__swipe-area"
        @touchstart.passive="onTouchStart"
        @touchend="onTouchEnd"
      >
      <div class="availability-calendar__toolbar">
        <button
          type="button"
          class="availability-calendar__nav"
          @click="goToPreviousMonth"
          :disabled="loading"
        >
          ←
        </button>
        <h4 class="availability-calendar__title">{{ monthLabel }}</h4>
        <button
          type="button"
          class="availability-calendar__nav"
          @click="goToNextMonth"
          :disabled="loading"
        >
          →
        </button>
      </div>

      <div
        v-if="selectable"
        class="availability-calendar__notice"
        :class="noticeClass"
        role="status"
      >
        <div class="availability-calendar__notice-steps" aria-hidden="true">
          <span
            class="availability-calendar__notice-step"
            :class="{ 'availability-calendar__notice-step--active': selectionPhase === 'pick-check-in' }"
          >
            1
          </span>
          <span class="availability-calendar__notice-line"></span>
          <span
            class="availability-calendar__notice-step"
            :class="{
              'availability-calendar__notice-step--active': selectionPhase === 'pick-check-out',
              'availability-calendar__notice-step--done': selectionPhase === 'complete'
            }"
          >
            2
          </span>
        </div>
        <p class="availability-calendar__notice-text">{{ selectionHint }}</p>
      </div>

      <div v-if="selectable && selectionMessage" class="availability-calendar__feedback" role="alert">
        {{ selectionMessage }}
      </div>

      <div class="availability-calendar__panel">
        <div v-if="loading" class="availability-calendar__loading">
          <div class="availability-calendar__loading-inner">
            <div class="availability-calendar__spinner" aria-hidden="true"></div>
            <div>Lade Verfügbarkeiten…</div>
          </div>
        </div>

        <div class="availability-calendar__weekdays">
          <span>Mo</span>
          <span>Di</span>
          <span>Mi</span>
          <span>Do</span>
          <span>Fr</span>
          <span>Sa</span>
          <span>So</span>
        </div>

        <div
          class="availability-calendar__grid"
          :class="{ 'availability-calendar__grid--selectable': selectable }"
          @mouseleave="selectable ? handleDayLeave() : undefined"
        >
          <div
            v-for="day in calendarDays"
            :key="day.key"
            class="availability-calendar__day"
            :class="{
              'availability-calendar__day--booked': day.isBooked,
              'availability-calendar__day--arrival': day.isArrival,
              'availability-calendar__day--departure': day.isDeparture,
              'availability-calendar__day--muted':
                day.isPast ||
                (!day.isCurrentMonth && !day.isBooked && !day.isArrival && !day.isDeparture),
              'availability-calendar__day--clickable': day.isClickable,
              'availability-calendar__day--range-start': day.isRangeStart,
              'availability-calendar__day--range-end': day.isRangeEnd,
              'availability-calendar__day--in-range': day.isInRange,
              'availability-calendar__day--hover-end': day.isHoverEnd && !day.isRangeEnd,
              'availability-calendar__day--in-hover-range': day.isInHoverRange,
              'availability-calendar__day--in-hover-range-invalid': day.isInHoverRangeInvalid
            }"
            @click="day.isClickable ? handleDayClick(day) : undefined"
            @mouseenter="selectable ? handleDayHover(day) : undefined"
          >
            <span class="availability-calendar__day-number">{{ day.label }}</span>
            <span
              v-if="!selectable && (day.isBooked || day.isDeparture)"
              class="availability-calendar__day-status"
            >
              <span v-if="day.isArrival && day.isDeparture">An/Ab</span>
              <span v-else-if="day.isArrival">Anreise</span>
              <span v-else-if="day.isDeparture">Abreise</span>
              <span v-else>Belegt</span>
            </span>
            <span
              v-else-if="selectable && day.isRangeStart"
              class="availability-calendar__day-status availability-calendar__day-status--selected"
            >
              Anreise
            </span>
            <span
              v-else-if="selectable && (day.isRangeEnd || day.isHoverEnd)"
              class="availability-calendar__day-status availability-calendar__day-status--selected"
            >
              Abreise
            </span>
          </div>
        </div>
      </div>

      <div class="availability-calendar__footer">
        <div v-if="selectable && checkIn" class="availability-calendar__selection">
          <div class="availability-calendar__selection-dates">
            <span class="availability-calendar__selection-label">Gewählter Zeitraum</span>
            <span class="availability-calendar__selection-value">
              {{ formatDateDisplay(checkIn) }}
              <span v-if="checkOut"> – {{ formatDateDisplay(checkOut) }}</span>
              <span v-else class="availability-calendar__selection-pending"> · Abreise wählen</span>
            </span>
          </div>
          <button type="button" class="availability-calendar__clear" @click="clearSelection">
            Löschen
          </button>
        </div>

        <div class="availability-calendar__legend" aria-label="Legende">
          <div class="availability-calendar__legend-item">
            <span class="availability-calendar__legend-swatch availability-calendar__legend-swatch--booked"></span>
            <span>Belegt</span>
          </div>
          <div class="availability-calendar__legend-item">
            <span class="availability-calendar__legend-swatch availability-calendar__legend-swatch--arrival"></span>
            <span>Anreise</span>
          </div>
          <div class="availability-calendar__legend-item">
            <span class="availability-calendar__legend-swatch availability-calendar__legend-swatch--departure"></span>
            <span>Abreise</span>
          </div>
          <div class="availability-calendar__legend-item">
            <span class="availability-calendar__legend-swatch availability-calendar__legend-swatch--free"></span>
            <span>Frei</span>
          </div>
          <div v-if="selectable" class="availability-calendar__legend-item">
            <span class="availability-calendar__legend-swatch availability-calendar__legend-swatch--selected"></span>
            <span>Ihre Auswahl</span>
          </div>
        </div>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, toRef } from 'vue'
import { useApi } from '../../composables/useApi.js'
import {
  useAvailabilityCalendar,
  formatDateDisplay
} from '../../composables/useAvailabilityCalendar.js'

export default {
  props: {
    apartment: {
      type: Object,
      default: null
    },
    selectable: {
      type: Boolean,
      default: false
    },
    checkIn: {
      type: String,
      default: ''
    },
    checkOut: {
      type: String,
      default: ''
    },
    active: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:checkIn', 'update:checkOut'],
  setup(props, { emit }) {
    const api = useApi()
    const active = toRef(props, 'active')
    const checkInRef = toRef(props, 'checkIn')
    const checkOutRef = toRef(props, 'checkOut')

    const {
      loading,
      error,
      monthLabel,
      calendarDays,
      selectionPhase,
      selectionMessage,
      goToPreviousMonth,
      goToNextMonth,
      handleDayClick,
      handleDayHover,
      handleDayLeave,
      clearSelection
    } = useAvailabilityCalendar({
      apiStore: api.store,
      apartment: () => props.apartment,
      selectable: props.selectable,
      getCheckIn: () => checkInRef.value,
      getCheckOut: () => checkOutRef.value,
      onRangeChange: ({ checkIn, checkOut }) => {
        emit('update:checkIn', checkIn)
        emit('update:checkOut', checkOut)
      },
      active: () => active.value
    })

    const selectionHint = computed(() => {
      if (selectionPhase.value === 'pick-check-in') {
        return 'Wählen Sie den Tag Ihrer Anreise.'
      }
      if (selectionPhase.value === 'pick-check-out') {
        return 'Wählen Sie den Tag Ihrer Abreise — der Zeitraum wird beim Darüberfahren angezeigt.'
      }
      return 'Zeitraum gewählt. Klicken Sie auf einen freien Tag, um neu zu starten.'
    })

    const noticeClass = computed(() => ({
      'availability-calendar__notice--complete': selectionPhase.value === 'complete'
    }))

    const SWIPE_MIN_DISTANCE = 48
    let touchStartX = 0
    let touchStartY = 0

    const onTouchStart = (event) => {
      if (event.touches.length !== 1) return
      touchStartX = event.touches[0].clientX
      touchStartY = event.touches[0].clientY
    }

    const onTouchEnd = (event) => {
      if (loading.value) return

      const touch = event.changedTouches?.[0]
      if (!touch) return

      const deltaX = touch.clientX - touchStartX
      const deltaY = touch.clientY - touchStartY

      if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE) return
      if (Math.abs(deltaX) <= Math.abs(deltaY)) return

      if (deltaX < 0) {
        goToNextMonth()
      } else {
        goToPreviousMonth()
      }
    }

    return {
      loading,
      error,
      monthLabel,
      calendarDays,
      selectionPhase,
      selectionMessage,
      goToPreviousMonth,
      goToNextMonth,
      handleDayClick,
      handleDayHover,
      handleDayLeave,
      clearSelection,
      formatDateDisplay,
      selectionHint,
      noticeClass,
      onTouchStart,
      onTouchEnd
    }
  }
}
</script>

<style scoped lang="scss">
.availability-calendar-wrapper {
  display: grid;
  gap: 16px;
}

.availability-calendar__swipe-area {
  display: grid;
  gap: 16px;
  touch-action: pan-y;
}

.availability-calendar__notice {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin: 0;
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8feff 0%, #f4fcfd 100%);
  border: 1px solid rgba(66, 217, 228, 0.28);
  border-radius: 12px;
}

.availability-calendar__notice--complete {
  background: linear-gradient(135deg, #f8fbf8 0%, #f3f8f4 100%);
  border-color: rgba(76, 140, 90, 0.28);
}

.availability-calendar__notice-steps {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 1px;
}

.availability-calendar__notice-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #d4d4d4;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  color: #888;
  transition: all 0.2s ease;
}

.availability-calendar__notice-step--active {
  border-color: #42d9e4;
  background: #42d9e4;
  color: #fff;
}

.availability-calendar__notice-step--done {
  border-color: #42d9e4;
  color: #42d9e4;
}

.availability-calendar__notice--complete .availability-calendar__notice-step--done {
  background: #42d9e4;
  color: #fff;
}

.availability-calendar__notice-line {
  width: 18px;
  height: 2px;
  background: #ddd;
  border-radius: 1px;
}

.availability-calendar__notice-text {
  margin: 0;
  padding-top: 3px;
  font-size: 14px;
  line-height: 1.5;
  color: #444;
}

.availability-calendar__notice--complete .availability-calendar__notice-text {
  color: #3d5c44;
}

.availability-calendar__feedback {
  margin: -8px 0 0;
  padding: 10px 14px;
  background: #fdf6f6;
  border: 1px solid rgba(185, 90, 90, 0.22);
  border-radius: 10px;
  color: #8f4d4d;
  font-size: 13px;
  line-height: 1.5;
}

.availability-calendar__panel {
  position: relative;
  padding: 16px;
  background: #fcfcfc;
  border: 1px solid #ececec;
  border-radius: 12px;
}

.availability-calendar__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.availability-calendar__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2f2f2f;
  text-transform: capitalize;
}

.availability-calendar__nav {
  border: 1px solid #42d9e4;
  background: #42d9e4;
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  transition: all 0.2s ease;
}

.availability-calendar__nav:hover:not([disabled]) {
  border-color: #35c4cf;
  background: #35c4cf;
  color: #fff;
}

.availability-calendar__nav[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
  border-color: rgba(66, 217, 228, 0.35);
  background: rgba(66, 217, 228, 0.35);
  color: rgba(255, 255, 255, 0.85);
}

.availability-calendar__footer {
  display: grid;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px solid #ececec;
}

.availability-calendar__legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.availability-calendar__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background: #fafafa;
  border: 1px solid #ebebeb;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #555;
  line-height: 1;
}

.availability-calendar__legend-swatch {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1px solid #e0e0e0;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  background: #fff;
}

.availability-calendar__legend-swatch--booked {
  background: linear-gradient(135deg, #f7ecec 0%, #efe2e2 100%);
  border-color: #dfcaca;
}

.availability-calendar__legend-swatch--arrival::after {
  content: '';
  position: absolute;
  inset: 0;
  background: #f7ecec;
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.availability-calendar__legend-swatch--departure::after {
  content: '';
  position: absolute;
  inset: 0;
  background: #f7ecec;
  clip-path: polygon(0 0, 100% 0, 0 100%);
}

.availability-calendar__legend-swatch--free {
  background: #fff;
}

.availability-calendar__legend-swatch--selected {
  background: #42d9e4;
  border-color: #42d9e4;
}

.availability-calendar__weekdays,
.availability-calendar__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
}

.availability-calendar__weekdays {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.availability-calendar__day {
  min-height: 68px;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  position: relative;
  overflow: hidden;
  z-index: 1;
  text-align: left;
  user-select: none;
}

.availability-calendar__grid--selectable .availability-calendar__day--clickable {
  cursor: pointer;
}

.availability-calendar__grid--selectable .availability-calendar__day--clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  border-color: #42d9e4;
}

.availability-calendar__day--booked {
  background: linear-gradient(135deg, #f7ecec 0%, #efe2e2 100%);
}

.availability-calendar__day--arrival::before,
.availability-calendar__day--departure::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.availability-calendar__day--arrival:not(.availability-calendar__day--departure)::before {
  background-color: #f7ecec;
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.availability-calendar__day--departure:not(.availability-calendar__day--arrival)::before {
  background-color: #f7ecec;
  clip-path: polygon(0 0, 100% 0, 0 100%);
}

.availability-calendar__day--arrival.availability-calendar__day--departure::before {
  display: none;
}

.availability-calendar__day--booked.availability-calendar__day--arrival:not(.availability-calendar__day--departure),
.availability-calendar__day--booked.availability-calendar__day--departure:not(.availability-calendar__day--arrival) {
  background: #fff;
  border-color: #e8e8e8;
}

.availability-calendar__day--range-start,
.availability-calendar__day--range-end,
.availability-calendar__day--hover-end:not(.availability-calendar__day--in-hover-range-invalid) {
  background: #42d9e4 !important;
  border-color: #42d9e4 !important;
}

.availability-calendar__day--range-start::before,
.availability-calendar__day--range-end::before,
.availability-calendar__day--hover-end::before {
  display: none;
}

.availability-calendar__day--in-range,
.availability-calendar__day--in-hover-range {
  background: rgba(66, 217, 228, 0.18) !important;
  border-color: rgba(66, 217, 228, 0.45) !important;
}

.availability-calendar__day--in-range::before,
.availability-calendar__day--in-hover-range::before {
  display: none;
}

.availability-calendar__day--in-hover-range-invalid {
  background: rgba(185, 90, 90, 0.12) !important;
  border-color: rgba(185, 90, 90, 0.35) !important;
}

.availability-calendar__day--in-hover-range-invalid::before {
  display: none;
}

.availability-calendar__day--hover-end.availability-calendar__day--in-hover-range-invalid {
  background: rgba(185, 90, 90, 0.28) !important;
  border-color: rgba(185, 90, 90, 0.55) !important;
}

.availability-calendar__day--hover-end.availability-calendar__day--in-hover-range-invalid .availability-calendar__day-number,
.availability-calendar__day--hover-end.availability-calendar__day--in-hover-range-invalid .availability-calendar__day-status {
  color: #8f4d4d;
}

.availability-calendar__day--range-start .availability-calendar__day-number,
.availability-calendar__day--range-end .availability-calendar__day-number,
.availability-calendar__day--hover-end .availability-calendar__day-number,
.availability-calendar__day--range-start .availability-calendar__day-status,
.availability-calendar__day--range-end .availability-calendar__day-status,
.availability-calendar__day--hover-end .availability-calendar__day-status {
  color: #fff;
}

.availability-calendar__loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  z-index: 50;
}

.availability-calendar__loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #444;
}

.availability-calendar__spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 4px solid rgba(0, 0, 0, 0.08);
  border-top-color: #b95a5a;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.availability-calendar__day--muted {
  opacity: 0.45;
}

.availability-calendar__day--muted.availability-calendar__day--booked,
.availability-calendar__day--muted.availability-calendar__day--arrival,
.availability-calendar__day--muted.availability-calendar__day--departure {
  opacity: 1;
}

.availability-calendar__day--muted:not(.availability-calendar__day--booked):not(.availability-calendar__day--arrival):not(.availability-calendar__day--departure)
  .availability-calendar__day-number {
  color: #999;
}

.availability-calendar__day-number {
  font-weight: 600;
  color: #1d1d1d;
  position: relative;
  z-index: 3;
}

.availability-calendar__day-status {
  font-size: 10px;
  font-weight: 700;
  color: #8f4d4d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  position: relative;
  z-index: 3;
}

.availability-calendar__day-status--selected {
  color: #fff;
}

.availability-calendar__selection {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 14px;
  background: linear-gradient(135deg, #f4fcfd 0%, #eefafb 100%);
  border: 1px solid rgba(66, 217, 228, 0.25);
  border-radius: 10px;
}

.availability-calendar__selection-dates {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.availability-calendar__selection-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6a8a8f;
}

.availability-calendar__selection-value {
  font-size: 14px;
  font-weight: 600;
  color: #2f2f2f;
}

.availability-calendar__selection-pending {
  font-weight: 500;
  color: #6a8a8f;
}

.availability-calendar__clear {
  appearance: none;
  border: 1px solid #d8d8d8;
  background: #fff;
  padding: 7px 14px;
  border-radius: 999px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.availability-calendar__clear:hover {
  border-color: #bbb;
  color: #333;
  background: #fafafa;
}

@media (max-width: 520px) {
  .availability-calendar__notice {
    flex-direction: column;
    gap: 10px;
  }

  .availability-calendar__panel {
    padding: 10px 6px;
  }

  .availability-calendar__weekdays,
  .availability-calendar__grid {
    gap: 4px;
  }

  .availability-calendar__weekdays {
    margin-bottom: 4px;
    font-size: 10px;
  }

  .availability-calendar__day {
    min-height: 0;
    aspect-ratio: 1;
    height: auto;
    padding: 3px;
    gap: 0;
    border-radius: 6px;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  .availability-calendar__day-number {
    font-size: 13px;
    line-height: 1;
  }

  /* Labels passen nicht in quadratische Zellen – Farben/Legende reichen auf Mobile */
  .availability-calendar__day-status {
    display: none;
  }

  .availability-calendar__grid--selectable
    .availability-calendar__day--range-start
    .availability-calendar__day-status--selected,
  .availability-calendar__grid--selectable
    .availability-calendar__day--range-end
    .availability-calendar__day-status--selected,
  .availability-calendar__grid--selectable
    .availability-calendar__day--hover-end
    .availability-calendar__day-status--selected {
    display: block;
    position: absolute;
    bottom: 2px;
    left: 0;
    right: 0;
    font-size: 7px;
    line-height: 1;
    letter-spacing: 0;
    text-align: center;
  }

  .availability-calendar__legend {
    justify-content: flex-start;
  }

  .availability-calendar__legend-item {
    font-size: 11px;
    padding: 6px 10px;
  }
}
</style>
